import { isRecord } from './isRecord';
import { type ServerAiBuilderChatEventDto } from './serverAiBuilderChatDtos';

export type ServerAiBuilderChatSseParser = {
  push: (chunk: string) => ServerAiBuilderChatEventDto[];
  flush: () => ServerAiBuilderChatEventDto[];
};

type SseBlock = {
  eventName: string | null;
  dataLines: string[];
};

export const createServerAiBuilderChatSseParser =
  (): ServerAiBuilderChatSseParser => {
    let pendingText = '';
    let block: SseBlock = createEmptyBlock();

    const dispatchBlock = () => {
      const event = createEventFromBlock(block);

      block = createEmptyBlock();

      return event;
    };

    const consumeLine = (line: string) => {
      if (line === '') {
        return dispatchBlock();
      }

      if (line.startsWith(':')) {
        return null;
      }

      const { field, value } = parseSseField(line);

      switch (field) {
        case 'event':
          block.eventName = value;
          break;
        case 'data':
          block.dataLines.push(value);
          break;
      }

      return null;
    };

    const push = (chunk: string) => {
      const events: ServerAiBuilderChatEventDto[] = [];

      pendingText += chunk;

      let lineStartIndex = 0;

      for (let index = 0; index < pendingText.length; index += 1) {
        const char = pendingText[index];

        if (char === '\n') {
          events.push(
            ...compactEvent(
              consumeLine(pendingText.slice(lineStartIndex, index)),
            ),
          );
          lineStartIndex = index + 1;
          continue;
        }

        if (char !== '\r') continue;

        if (index === pendingText.length - 1) break;

        events.push(
          ...compactEvent(
            consumeLine(pendingText.slice(lineStartIndex, index)),
          ),
        );

        if (pendingText[index + 1] === '\n') {
          index += 1;
        }

        lineStartIndex = index + 1;
      }

      pendingText = pendingText.slice(lineStartIndex);

      return events;
    };

    const flush = () => {
      const events: ServerAiBuilderChatEventDto[] = [];

      if (pendingText.endsWith('\r')) {
        events.push(
          ...compactEvent(
            consumeLine(pendingText.slice(0, pendingText.length - 1)),
          ),
        );
      } else if (pendingText.length > 0) {
        events.push(...compactEvent(consumeLine(pendingText)));
      }

      pendingText = '';
      events.push(...compactEvent(dispatchBlock()));

      return events;
    };

    return {
      push,
      flush,
    };
  };

export async function* parseServerAiBuilderChatSseStream(
  stream: ReadableStream<Uint8Array>,
): AsyncIterable<ServerAiBuilderChatEventDto> {
  const decoder = new TextDecoder();
  const parser = createServerAiBuilderChatSseParser();
  const reader = stream.getReader();
  let completed = false;

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) break;
      if (!value) continue;

      for (const event of parser.push(
        decoder.decode(value, { stream: true }),
      )) {
        yield event;
      }
    }

    const trailingText = decoder.decode();

    if (trailingText.length > 0) {
      for (const event of parser.push(trailingText)) {
        yield event;
      }
    }

    for (const event of parser.flush()) {
      yield event;
    }

    completed = true;
  } finally {
    // 기다리지 않는다 — 응답이 아직 열린 채 cancel 하면 iOS WebKit 에선 끝나지 않을 수 있고, 그러면
    // respond 에서 먼저 빠져나온 소비자(생성기)의 return 이 끝나지 않아 실행이 영영 정리되지 않는다.
    if (!completed) {
      // The stream may already be aborted by fetch; releasing the lock still matters.
      reader.cancel().catch(() => undefined);
    }

    reader.releaseLock();
  }
}

const createEmptyBlock = (): SseBlock => ({
  eventName: null,
  dataLines: [],
});

const parseSseField = (line: string) => {
  const separatorIndex = line.indexOf(':');

  if (separatorIndex < 0) {
    return {
      field: line,
      value: '',
    };
  }

  const rawValue = line.slice(separatorIndex + 1);

  return {
    field: line.slice(0, separatorIndex),
    value: rawValue.startsWith(' ') ? rawValue.slice(1) : rawValue,
  };
};

const createEventFromBlock = (
  block: SseBlock,
): ServerAiBuilderChatEventDto | null => {
  if (block.dataLines.length === 0) return null;

  let data: unknown;

  try {
    data = JSON.parse(block.dataLines.join('\n')) as unknown;
  } catch {
    return null;
  }

  if (!isRecord(data)) {
    return null;
  }

  if (typeof data.type === 'string') {
    return data as ServerAiBuilderChatEventDto;
  }

  if (!block.eventName) {
    return null;
  }

  return {
    ...data,
    type: block.eventName,
  } as ServerAiBuilderChatEventDto;
};

const compactEvent = (
  event: ServerAiBuilderChatEventDto | null,
): ServerAiBuilderChatEventDto[] => {
  return event ? [event] : [];
};
